// @steered AudibleSecurityContext 1.2 2026-09-15
/**
 * Derives which preview surfaces (Web / iOS app / Android app) apply to a
 * G2S2 change, from the record's clientId (and, when known, applicationType).
 *
 * A record is keyed by clientId, so a change written for `AudiblePDP` can only
 * ever render on the Audible web PDP; offering an iOS/Android preview for it is
 * misleading. Conversely an `AudibleApp_*` record renders only in the native
 * apps. Unknown or missing clientIds fall back to Web (the safe default the UI
 * always had).
 */

import type { AppetizePlatform } from '../appetize/appetize.types';

/** 'web' plus the native platforms AppetizePreview can render -- derived so the
 *  two unions cannot drift apart. */
export type PreviewSurface = 'web' | AppetizePlatform;

export const ALL_PREVIEW_SURFACES: readonly PreviewSurface[] = ['web', 'ios', 'android'];

export const PREVIEW_SURFACE_LABELS: Record<PreviewSurface, string> = {
  web: 'Web',
  ios: 'iOS app',
  android: 'Android app',
};

/** G2S2 clientIds that render exclusively on a web page. */
const WEB_CLIENT_IDS = new Set(['AudiblePDP', 'AudibleListView', 'AmazonHomesteadDP']);

/** G2S2 clientId prefixes that render exclusively inside the native Audible apps. */
const NATIVE_CLIENT_ID_PREFIXES = ['AudibleApp'];

/** applicationType enum values narrowing a native clientId to one platform. */
const IOS_APPLICATION_TYPES = new Set(['Apple_App']);
const ANDROID_APPLICATION_TYPES = new Set([
  'Android_App',
  'Android_GooglePlay_App',
  'Fire',
  'Samsung',
]);

export interface PreviewSurfaceInput {
  /** Exact G2S2 clientId from the record / orchestrator config block, e.g. "AudiblePDP". */
  clientId?: string | null;
  /** Optional abods_config_applicationType enum value, e.g. "Apple_App". */
  applicationType?: string | null;
  /**
   * Whether a native (Appetize) preview can actually be rendered for this
   * session — i.e. the validation URL carries a g2s2StageVersionOverride.
   * When false, native surfaces are dropped and Web is offered instead.
   */
  nativePreviewSupported?: boolean;
}

/**
 * Returns the ordered list of surfaces to offer. Never empty: falls back to ['web'].
 */
export function getPreviewSurfaces(input: PreviewSurfaceInput): PreviewSurface[] {
  const clientId = (input.clientId || '').trim();
  const appType = (input.applicationType || '').trim();
  const nativeOk = input.nativePreviewSupported !== false;

  let surfaces: PreviewSurface[];

  if (!clientId) {
    surfaces = ['web'];
  } else if (WEB_CLIENT_IDS.has(clientId)) {
    surfaces = ['web'];
  } else if (NATIVE_CLIENT_ID_PREFIXES.some(p => clientId.startsWith(p))) {
    if (IOS_APPLICATION_TYPES.has(appType)) {
      surfaces = ['ios'];
    } else if (ANDROID_APPLICATION_TYPES.has(appType)) {
      surfaces = ['android'];
    } else {
      surfaces = ['ios', 'android'];
    }
  } else {
    // Unknown clientId: do not guess a native build for it.
    surfaces = ['web'];
  }

  if (!nativeOk) {
    surfaces = surfaces.filter(s => s === 'web');
  }
  return surfaces.length > 0 ? surfaces : ['web'];
}

/**
 * Picks the surface to show: keeps `preferred` when it is offered, otherwise the
 * first offered surface.
 */
export function pickPreviewSurface(
  preferred: PreviewSurface | null | undefined,
  available: readonly PreviewSurface[]
): PreviewSurface {
  if (preferred && available.includes(preferred)) return preferred;
  return available[0] ?? 'web';
}
